import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  SignalWifi4Bar as ConnectionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

function UpdateConnectionStatusModal({
  open,
  onClose,
  deviceData = null,
  connectionStatuses = [],
  onUpdateConnectionStatus,
  loading = false,
}) {
  const { t } = useTranslation();
  const [connectionStatus, setConnectionStatus] = useState('');
  const [reason, setReason] = useState('');

  // Reset form when modal opens with device data
  useEffect(() => {
    if (open && deviceData) {
      setConnectionStatus(deviceData.connectionStatus ?? '');
      setReason('');
    }
  }, [open, deviceData]);

  if (!deviceData) return null;

  const handleSubmit = () => {
    onUpdateConnectionStatus(deviceData.id, connectionStatus, reason);
  };

  const handleClose = () => {
    setConnectionStatus('');
    setReason('');
    onClose();
  };

  const isStatusChanged = connectionStatus !== '' && connectionStatus !== deviceData.connectionStatus;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <ConnectionIcon />
        {t('devices.connectionStatusModal.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Device Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('devices.table.deviceCode')}
            </Typography>
            <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
              {deviceData.deviceCode || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('devices.table.name')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {deviceData.name || '-'}
            </Typography>
          </Box>

          {/* Connection Status Select */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="connection-status-label">
              {t('devices.connectionStatusModal.newStatus')}
            </InputLabel>
            <Select
              labelId="connection-status-label"
              id="connectionStatus"
              name="connectionStatus"
              value={connectionStatus}
              onChange={(e) => setConnectionStatus(e.target.value)}
              label={t('devices.connectionStatusModal.newStatus')}
              data-testid="connection-status-select"
            >
              {connectionStatuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Reason TextField */}
          <TextField
            label={t('devices.connectionStatusModal.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder={t('devices.connectionStatusModal.reasonPlaceholder')}
            inputProps={{
              id: 'reason',
              name: 'reason',
              'data-testid': 'connection-status-modal-input-reason',
              maxLength: 500,
            }}
            helperText={`${reason.length}/500`}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          id="cancel"
          name="cancel"
          data-testid="connection-status-modal-button-cancel"
        >
          {t('devices.connectionStatusModal.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isStatusChanged || loading}
          id="update"
          name="update"
          data-testid="connection-status-modal-button-update"
        >
          {loading ? t('devices.connectionStatusModal.updating') : t('devices.connectionStatusModal.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateConnectionStatusModal;
